import React, { useLayoutEffect, useRef, useState } from "react";
import { SearchResponse } from "../shared/shared.models";
import { StyrandeDokument } from "./models";
import {
  ActiveSearchFilters,
  CheckboxTree,
  NoHits,
  Pagination,
  SearchHeader,
  ToggleList,
} from "../shared/components";
import { ClientOnly, useClientOnly } from "../shared/hooks";
import { SearchResultItem } from "./SearchResultItem";
import { SearchResultVardhandboken } from "./SearchResultVardhandboken";
import {
  selectActiveFilters,
  selectIsLoading,
  selectSearchResult,
} from "../shared/store/shared.reducer";
import { useAppSelector } from "../shared/store/store.hooks";
import { Sorting } from "../shared/components/Sorting";
interface Props {
  svgPathNext: string;
  svgPathPrev: string;
  searchIcon: string;
  svgArrow:string;
}
export const StyrandeDokumentComponent: React.VFC<Props> = ({ searchIcon, svgPathNext, svgPathPrev, svgArrow }) => {
  /*********************************
   * Variables
   *********************************/
  const pathName =
    typeof window !== "undefined" ? window.location.pathname : ""; // En tillfällig fix. Sökningen kommer inte att fungera utan javascript, eftersom vi behöver sökvägen till sidan.

  const isFirstRender = useRef(true);
  const [isFilterSectionOpen, setFilterSectionOpen] = useState(false);

  const searchResult = useAppSelector(
    selectSearchResult
  ) as SearchResponse<StyrandeDokument>;
  const isLoading = useAppSelector(selectIsLoading);
  const activeFiltersCount = useAppSelector(selectActiveFilters).length;
  const searchResultQueryForvaltning = new URLSearchParams(
    searchResult.query.parameters
  ).get("forvaltning");
  const { numberOfHits, sortOptions, pagination } = searchResult.documentList;
  const facet = searchResult.facet;
  const subjectFacets = searchResult.subjectFacets;
  const hidePagination = pagination.pages.length == 0;

  const searchResultClassList = isLoading
    ? "vgr-searchservice__results loading"
    : "vgr-searchservice__results";

  const isHydrated = useClientOnly();

  /*********************************
   * Functions
   *********************************/
  function toggleFilterPanel() {
    document.getElementsByTagName("html")[0].classList.toggle("backdrop");
    setFilterSectionOpen(!isFilterSectionOpen);
  }

  const getSearchQuery = () => {
    const queryString = searchResult.query.queryString;
    if (queryString !== "*") return `  "${searchResult.query.queryString}"`;
    return "*";
  };

  useLayoutEffect(() => {
    isFirstRender.current = false;
  }, []);
const chevron = (
  <svg aria-hidden={true} style={{ width: "16px", height: "16px" }}>
    <use xlinkHref={svgArrow}></use>
  </svg>
);
  /*********************************
   * Render
   *********************************/
  return (
    <>    <div className="container-fluid vgr-searchservice--wrapper">
      <div className="container">

        <div className="vgr-searchservice">
          <SearchHeader searchIcon={searchIcon} />
        </div>
      </div>
    </div>
      <div className="container">
        <div className="vgr-searchservice">
          <ClientOnly>
            <section
              id="searchfiltersection"
              aria-label="Sektion med olika valbara sökfilter"
              className="vgr-searchservice__filters"
              data-firstrender={isFirstRender.current}
              data-open={isFilterSectionOpen}
            >
              <div className="vgr-searchservice__closefilter">
                <button
                  className="vgr-searchservice__linkbutton"
                  onClick={() => toggleFilterPanel()}
                  type="button"
                >
                  {chevron} Stäng
                </button>
              </div>
              <ActiveSearchFilters />

              <ToggleList config={searchResult.includesFilters} svgArrow={svgArrow}/>

              {facet.children?.length > 0 && (
                <CheckboxTree
                  heading={facet.displayName}
                  tight={true}
                  treeId={facet.id}
                  items={facet.children}
                  svgArrow={svgArrow}
                />
              )}

              {subjectFacets.filters?.length > 0 && (
                <CheckboxTree
                  heading={subjectFacets.displayName}
                  tight={true}
                  treeId={subjectFacets.id}
                  items={subjectFacets.filters}
                  maxTopLevelItems={7}
                  svgArrow={svgArrow}
                />
              )}

              <div className="vgr-searchservice__mobile-filter-show-documents">
                <button type="button" onClick={() => toggleFilterPanel()}>
                  Visa {numberOfHits} dokument
                </button>
              </div>
            </section>
          </ClientOnly>

          <Sorting
            isHydrated={isHydrated}
            sortList={sortOptions}
            isLoading={isLoading}
            numberOfHits={numberOfHits}
            mobileFormClassName="vgr-searchservice__resultsorting-mobile"
            endPoint={pathName + "/FilterStyrandeDokument"}
            hiddenClassName=""
            sectionClassName="vgr-searchservice__resultsheadersection"
            resultHeaderClassName="vgr-searchservice__resultsheader"
            searchQuery={getSearchQuery()}
            desktopResultSortingClassName="vgr-searchservice__resultsorting"
            buttonClassName="vgr-searchservice__linkbutton"
            hasMobileButton={true}
            isFilterSectionOpen={isFilterSectionOpen}
            setFilterSectionOpen={setFilterSectionOpen}
            activeFiltersCount={activeFiltersCount}
            filterIcon={newFilterIcon}
            searchResultQueryForvaltning={searchResultQueryForvaltning}
          />

          <section
            tabIndex={0}
            aria-label="Sektion där sökresultat presenteras"
            id="searchResultList"
            className={
              isHydrated
                ? searchResultClassList
                : searchResultClassList + " ssr-only"
            }
          >
            {searchResult.documentList.documents.map((x) =>
              x.source === "Vårdhandboken" ? (
                <SearchResultVardhandboken
                  key={x.id + searchResult.query.parameters}
                  item={x}
                  svgArrow={svgArrow}
                />
              ) : (
                <SearchResultItem
                  key={x.id + searchResult.query.parameters}
                  item={x}
                  svgArrow={svgArrow}
                />
              )
            )}
            {searchResult.documentList.numberOfHits === 0 && <NoHits />}
            {searchResult.documentList.numberOfHits > 0 && (
              <div className="vgr-searchservice__box vgr-searchservice__infobox">
                <div>
                  <strong>Hittar du inte det du söker?</strong>
                </div>
                <p className="full-width-paragraph">
                  Här visas bara de dokument som upprättats som styrande dokument.
                  Checklistor, blanketter och andra stödjande dokument finns i Hitta
                  i arkivet. Där kan du också hitta dokument med begränsad
                  åtkomsträtt, enbart för din myndighet.
                </p>

                <a className="vgr-searchservice__box__info-link" href="https://hittaiarkivet.vgregion.se">
                  <span> Sök i hittaiarkivet.vgregion.se</span>
                </a>
              </div>
            )}
            {!hidePagination && (
              <Pagination
                scrollToId="searchResultsHeaderSection"
                cssClass="vgr-searchservice"
                endPoint={pathName + "/FilterStyrandeDokument"}
                searchResult={searchResult}
                svgPathNext={svgPathNext}
                svgPathPrev={svgPathPrev}
              />
            )}
          </section>
        </div>
      </div>
    </>
  );


};


const newFilterIcon = (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M2 3.99854C0.897333 3.99854 0 3.1012 0 1.99854C0 0.895868 0.897333 -0.00146484 2 -0.00146484C3.10267 -0.00146484 4 0.895868 4 1.99854C4 3.1012 3.10267 3.99854 2 3.99854ZM2 0.998535C1.44867 0.998535 1 1.4472 1 1.99854C1 2.54987 1.44867 2.99854 2 2.99854C2.55133 2.99854 3 2.54987 3 1.99854C3 1.4472 2.55133 0.998535 2 0.998535Z"
      fill="#005B89" />
    <path d="M2 9.99854C0.897333 9.99854 0 9.1012 0 7.99854C0 6.89587 0.897333 5.99854 2 5.99854C3.10267 5.99854 4 6.89587 4 7.99854C4 9.1012 3.10267 9.99854 2 9.99854ZM2 6.99854C1.44867 6.99854 1 7.4472 1 7.99854C1 8.54987 1.44867 8.99854 2 8.99854C2.55133 8.99854 3 8.54987 3 7.99854C3 7.4472 2.55133 6.99854 2 6.99854Z"
      fill="#005B89" />
    <path d="M2 15.9985C0.897333 15.9985 0 15.1012 0 13.9985C0 12.8959 0.897333 11.9985 2 11.9985C3.10267 11.9985 4 12.8959 4 13.9985C4 15.1012 3.10267 15.9985 2 15.9985ZM2 12.9985C1.44867 12.9985 1 13.4472 1 13.9985C1 14.5499 1.44867 14.9985 2 14.9985C2.55133 14.9985 3 14.5499 3 13.9985C3 13.4472 2.55133 12.9985 2 12.9985Z"
      fill="#005B89" />
    <path d="M5.5 2.99854C5.224 2.99854 5 2.77454 5 2.49854C5 2.22254 5.224 1.99854 5.5 1.99854H15.5C15.776 1.99854 16 2.22254 16 2.49854C16 2.77454 15.776 2.99854 15.5 2.99854H5.5Z"
      fill="#005B89" />
    <path d="M5.5 8.99854C5.224 8.99854 5 8.77454 5 8.49854C5 8.22254 5.224 7.99854 5.5 7.99854H15.5C15.776 7.99854 16 8.22254 16 8.49854C16 8.77454 15.776 8.99854 15.5 8.99854H5.5Z"
      fill="#005B89" />
    <path d="M5.5 14.9985C5.224 14.9985 5 14.7745 5 14.4985C5 14.2225 5.224 13.9985 5.5 13.9985H15.5C15.776 13.9985 16 14.2225 16 14.4985C16 14.7745 15.776 14.9985 15.5 14.9985H5.5Z"
      fill="#005B89" />
  </svg>
)